// File: src/components/TurnstileWidget.js
// Cloudflare Turnstile, wrapped so a form can treat it as one controlled value.
//
// The widget hands back a single-use token that expires after a few minutes, so
// the parent must reset it after any failed submit - otherwise the retry sends
// a token Cloudflare has already seen and the entrant gets a second, baffling
// rejection.

import React, { useEffect, useRef, useCallback } from 'react';
import { TURNSTILE_SITE_KEY } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

// Loads the script once per page, however many widgets mount.
let scriptPromise = null;
const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Turnstile script failed to load'));
    };
    if (!existing) document.head.appendChild(script);
  });

  return scriptPromise;
};

// `resetSignal` is a counter: bump it in the parent to force a fresh token.
function TurnstileWidget({ onToken, resetSignal = 0 }) {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Held in a ref so re-rendering the parent does not re-create the widget.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  const render = useCallback(async () => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return;

    try {
      const turnstile = await loadTurnstile();
      if (!containerRef.current) return; // unmounted while the script loaded

      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(''),
        'error-callback': () => onTokenRef.current('')
      });
    } catch (error) {
      console.error(error.message);
      onTokenRef.current('');
    }
  }, []);

  useEffect(() => {
    render();
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [render]);

  // Tokens are single-use, so a failed submit needs a new one.
  useEffect(() => {
    if (resetSignal > 0 && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onTokenRef.current('');
    }
  }, [resetSignal]);

  // Without a site key the form cannot be submitted at all - the server refuses
  // unverified writes - so say so rather than showing an empty space.
  if (!TURNSTILE_SITE_KEY) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded text-sm">
        {t('entryTurnstileMissing')}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{t('entryHumanCheck')}</label>
      <div ref={containerRef} />
    </div>
  );
}

export default TurnstileWidget;
